import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import AuthContextProvider from './contexts/authContext';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import PropTypes from 'prop-types';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorFallback from './pages/ErrorFallback';
import { HelmetProvider } from 'react-helmet-async';

// vite.config.js inyecta window.__PRERENDER__ = true durante la captura de Puppeteer.
// Con speedy:false, Emotion escribe CSS como text nodes en <style> en lugar de
// via CSSStyleSheet.insertRule(), haciéndolos visibles en outerHTML para Puppeteer.
// En el browser normal, window.__PRERENDER__ es undefined → speedy:true.
const emotionCache = createCache({
  key: 'css',
  speedy: !window.__PRERENDER__,
});

const stepperTheme = {
  baseStyle: {
    // select the indicator part
    indicator: {
      borderColor: 'gray.500',
      border: 0,
      ring: 2,
      ringColor: 'gray.300',
    },
  },
};

const theme = extendTheme({
  components: {
    Stepper: stepperTheme,
  },
  colors: {
    brand: {
      50: '#00FEEE',
      100: '#00F3E0',
      200: '#00E8D7',
      300: '#00CBB9',
      400: '#00D6C2',
      500: '#00DDCE',
      600: '#00C0B0',
      700: '#00B5A7',
      800: '#00B5A7',
      900: '#00AA9E',
    },
    teal: {
      50: '#E6FFFA',
      100: '#B2F5EA',
      200: '#81E6D9',
      300: '#4FD1C5',
      400: '#38B2AC',
      500: '#00DDCE',
      600: '#00C0B0',
      700: '#00AA9E',
      800: '#00968A',
      900: '#007F72',
    },
    primary: {
      50: '#B1BEDF',
      100: '#A2B6E9',
      200: '#93AEEF',
      300: '#84A6F5',
      400: '#759EFB',
      500: '#012990',
      600: '#012990',
      700: '#01177B',
      800: '#01177B',
      900: '#01096A',
    },
    secondary: {
      50: '#F7F18E',
      100: '#F7E071',
      200: '#F7CF54',
      300: '#F7BE36',
      400: '#F7AD19',
      500: '#F7AD19',
      600: '#F79900',
      700: '#F78800',
      800: '#F78800',
      900: '#F77700',
    },
    bubbleGum: {
      50: '#F77EAE',
      100: '#F76C9C',
      200: '#F75A8A',
      300: '#F74879',
      400: '#F73668',
      500: '#F73668',
      600: '#F7133D',
      700: '#F70C2C',
      800: '#F70C2C',
      900: '#F7061B',
    },
  },
});

const Providers = ({ children }) => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={new QueryClient()}>
        <AuthContextProvider>
          <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
            <CacheProvider value={emotionCache}>
            <ChakraProvider theme={theme}>
              <ErrorBoundary FallbackComponent={ErrorFallback}>{children}</ErrorBoundary>
            </ChakraProvider>
            </CacheProvider>
          </GoogleOAuthProvider>
        </AuthContextProvider>
        {/* React Query DevTools */}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </HelmetProvider>
  );
};

Providers.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Providers;
